'use client';

import React from 'react';
import { buildStayLinkFromData } from '@/lib/affiliates/builders/trip';
import AffiliateLink from '@/components/common/AffiliateLink';
import { getProviderButtonStyles } from './providerButtonStyles';

/**
 * Компонент кнопки/ссылки для размещения с автоматической генерацией партнёрских ссылок Trip.com
 */
export default function StayLinkButton({
    children,
    className = '',
    variant = 'primary', // 'primary', 'secondary', 'chip', 'card'
    countrySlug,
    citySlug,
    siteLang,
    countryData,
    cityData,
    affiliateProvider,
    linkParams = {},
    onClick,
    disabled = false,
    ...props
}) {
    // Генерируем ссылку
    const href = buildStayLinkFromData({
        countrySlug,
        citySlug,
        siteLang,
        countryData,
        cityData,
        affiliateProvider,
        linkParams
    });

    // Аналитика клика целиком в AffiliateLink (единый affiliate_click с
    // vertical='stay'). Отдельный accommodation_click убран 2026-08-11 (аудит
    // §4.5) — дублировал событие под третьим именем и дробил воронку жилья.
    // mode передаём как position — это единственное Trip-поле, нужное в GA;
    // subId вшит в href и виден в статистике Travelpayouts.
    const handleClick = (e) => {
        if (onClick) {
            onClick(e);
        }
    };

    const variantStyles = getProviderButtonStyles(variant, 'trip');

    // Если ссылка не сгенерировалась или кнопка отключена
    if (href === '#' || disabled) {
        return (
            <button
                className={`${variantStyles} ${className} opacity-50 cursor-not-allowed`}
                disabled
                {...props}
            >
                {children}
            </button>
        );
    }

    // Рендерим через AffiliateLink: единое событие affiliate_click + rel="...sponsored"
    // (требование Google) + единый fbq Lead.
    return (
        <AffiliateLink
            href={href}
            provider={affiliateProvider?.code || 'trip'}
            vertical="stay"
            country={countrySlug}
            position={linkParams.mode || 'base'}
            className={`${variantStyles} ${className} no-underline`.replace(/\s+/g, ' ').trim()}
            onClick={handleClick}
            {...props}
        >
            {children}
        </AffiliateLink>
    );
}

/**
 * Компонент чипа для фильтров размещения
 */
export function StayFilterChip({
    label,
    type, // 'keyword', 'poi', 'distance'
    value, // keyword, poiKey, или координаты
    citySlug,
    countrySlug,
    siteLang,
    countryData,
    cityData,
    affiliateProvider,
    isActive = false,
    onClick,
    ...props
}) {
    // Используем citySlug если есть, иначе countrySlug
    const locationSlug = citySlug || countrySlug;

    const linkParams = {
        mode: type,
        subId: `${locationSlug}-${type}-${value}`,
        ...(type === 'keyword' && { keyword: value }),
        ...(type === 'poi' && { poiKey: value }),
        ...(type === 'distance' && value?.lat && value?.lon && {
            lat: value.lat,
            lon: value.lon
        })
    };

    return (
        <StayLinkButton
            variant="chip"
            countrySlug={countrySlug}
            citySlug={citySlug}
            siteLang={siteLang}
            countryData={countryData}
            cityData={cityData}
            affiliateProvider={affiliateProvider}
            linkParams={linkParams}
            onClick={onClick}
            className={`${isActive ? 'bg-blue-100 text-blue-700 border-blue-300' : ''}`}
            {...props}
        >
            {label}
        </StayLinkButton>
    );
}

/**
 * Компонент карточки размещения
 */
export function StayCard({
    title,
    description,
    image,
    type, // тип размещения для генерации ссылки
    citySlug,
    countrySlug,
    siteLang,
    countryData,
    cityData,
    affiliateProvider,
    ...props
}) {
    // Используем citySlug если есть, иначе countrySlug
    const locationSlug = citySlug || countrySlug;

    const linkParams = {
        mode: 'keyword',
        keyword: type,
        subId: `${locationSlug}-${type}`
    };

    return (
        <StayLinkButton
            variant="card"
            countrySlug={countrySlug}
            citySlug={citySlug}
            siteLang={siteLang}
            countryData={countryData}
            cityData={cityData}
            affiliateProvider={affiliateProvider}
            linkParams={linkParams}
            {...props}
        >
            <div className="text-center">
                {image && image.trim() !== '' && (
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden">
                        <img
                            src={image}
                            alt={title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}
                <h3 className="text-lg font-bold mb-2">{title}</h3>
                {description && (
                    <p className="text-sm text-gray-600">{description}</p>
                )}
            </div>
        </StayLinkButton>
    );
}
